import React, { useEffect, useRef, useState } from 'react';
import { Loader2, Mic, MicOff, Minus, Phone, PhoneIncoming, PhoneOff } from 'lucide-react';
import { useVoiceCall } from '../contexts/VoiceCallContext';
import { cn } from '../lib/utils';
import { Button } from './ui/button.tsx';
import { Card } from './ui/card.tsx';

// Per-state look: card tint, icon colour, minimised button fill.
const TONES = {
  ended: {
    card: 'border-red-500/40',
    tint: 'bg-red-500/10',
    icon: 'text-red-600 dark:text-red-400',
    bubble: 'bg-red-600 hover:bg-red-600/90 text-white',
  },
  incoming: {
    card: 'border-amber-500/40',
    tint: 'bg-amber-500/10',
    icon: 'text-amber-600 dark:text-amber-400',
    bubble: 'bg-amber-500 hover:bg-amber-500/90 text-white',
  },
  dialling: {
    card: 'border-yellow-500/40',
    tint: 'bg-yellow-500/10',
    icon: 'text-yellow-600 dark:text-yellow-400',
    bubble: 'bg-yellow-500 hover:bg-yellow-500/90 text-white',
  },
  muted: {
    card: 'border-amber-500/40',
    tint: 'bg-amber-500/10',
    icon: 'text-amber-600 dark:text-amber-400',
    bubble: 'bg-amber-500 hover:bg-amber-500/90 text-white',
  },
  live: {
    card: 'border-emerald-500/40',
    tint: 'bg-emerald-500/10',
    icon: 'text-emerald-600 dark:text-emerald-400',
    bubble: 'bg-emerald-600 hover:bg-emerald-600/90 text-white',
  },
};

function resolveState({ isCallEndedNotice, isIncomingCall, isDialling, isMuted }) {
  if (isCallEndedNotice) return 'ended';
  if (isIncomingCall) return 'incoming';
  if (isDialling) return 'dialling';
  if (isMuted) return 'muted';
  return 'live';
}

/**
 * Floating call window beside the support chat button: incoming, dialling,
 * live and "call ended" states with their controls. Minimises to a round
 * button coloured by state; a new incoming call always re-opens it.
 */
const CallWidget = () => {
  const {
    isCallActive,
    isCallEndedNotice,
    isDialling,
    callEndedNotice,
    isIncomingCall,
    activeCallClient,
    callDirection,
    isMuted,
    hangUp,
    acceptIncoming,
    rejectIncoming,
    setMute,
  } = useVoiceCall();
  const [minimised, setMinimised] = useState(false);

  const displayClient = isCallEndedNotice ? (callEndedNotice || activeCallClient) : activeCallClient;
  const visible = Boolean(displayClient) && (isCallEndedNotice || isCallActive);

  // Never let an incoming call hide behind the minimised button.
  const wasIncoming = useRef(false);
  useEffect(() => {
    if (isIncomingCall && !wasIncoming.current) setMinimised(false);
    wasIncoming.current = Boolean(isIncomingCall);
  }, [isIncomingCall]);

  // Next call starts expanded.
  useEffect(() => {
    if (!visible) setMinimised(false);
  }, [visible]);

  if (!visible) return null;

  const state = resolveState({ isCallEndedNotice, isIncomingCall, isDialling, isMuted });
  const tone = TONES[state];
  const isInbound = callDirection === 'inbound' || isIncomingCall;
  const name = displayClient.name || 'Unknown caller';

  const StateIcon = {
    ended: PhoneOff,
    incoming: PhoneIncoming,
    dialling: Loader2,
    muted: MicOff,
    live: Phone,
  }[state];
  const iconAnimation = {
    incoming: 'animate-phone-ring',
    dialling: 'animate-spin',
    muted: 'animate-call-muted',
    live: 'animate-phone-ring',
  }[state];
  const label = {
    ended: 'Call ended',
    incoming: 'Incoming call',
    dialling: 'Dialling',
    muted: 'Muted',
    live: isInbound ? 'Inbound call' : 'On call',
  }[state];

  if (minimised) {
    return (
      <Button
        type="button"
        size="icon"
        className={cn('h-12 w-12 rounded-full shadow-lg', tone.bubble)}
        onClick={() => setMinimised(false)}
        aria-label={`${label}: ${name}. Show call window`}
        title={`${label}: ${name}`}
      >
        <StateIcon className={cn('h-5 w-5', iconAnimation)} />
      </Button>
    );
  }

  return (
    <Card
      className={cn('w-[280px] max-w-[calc(100vw-2rem)] p-0 shadow-lg overflow-hidden', tone.card)}
      role="region"
      aria-label="Call"
    >
      <div className={tone.tint}>
      <div className="flex items-center gap-2 px-3 pt-2.5">
        <StateIcon className={cn('h-4 w-4 shrink-0', tone.icon, iconAnimation)} />
        <p className="flex-1 min-w-0 text-xs font-medium text-muted-foreground truncate">{label}</p>
        {state !== 'ended' && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7 shrink-0"
            onClick={() => setMinimised(true)}
            aria-label="Minimise call window"
          >
            <Minus className="h-4 w-4" />
          </Button>
        )}
      </div>
      <p className="px-3 pb-2.5 pt-0.5 text-sm font-semibold truncate">{name}</p>
      </div>

      {state !== 'ended' && (
        <div className="flex gap-2 border-t border-border px-3 py-2.5">
          {state === 'incoming' && (
            <>
              <Button
                type="button"
                size="sm"
                className="flex-1 bg-emerald-600 hover:bg-emerald-600/90 text-white"
                onClick={acceptIncoming}
              >
                <Phone className="h-4 w-4 mr-1.5" />
                Accept
              </Button>
              <Button type="button" size="sm" variant="outline" className="flex-1" onClick={rejectIncoming}>
                Decline
              </Button>
            </>
          )}
          {state === 'dialling' && (
            <Button type="button" size="sm" variant="outline" className="flex-1" onClick={hangUp}>
              Cancel
            </Button>
          )}
          {(state === 'live' || state === 'muted') && (
            <>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="flex-1"
                onClick={() => setMute(!isMuted)}
              >
                {isMuted ? <Mic className="h-4 w-4 mr-1.5" /> : <MicOff className="h-4 w-4 mr-1.5" />}
                {isMuted ? 'Unmute' : 'Mute'}
              </Button>
              <Button type="button" size="sm" variant="destructive" className="flex-1" onClick={hangUp}>
                <PhoneOff className="h-4 w-4 mr-1.5" />
                Hang up
              </Button>
            </>
          )}
        </div>
      )}
    </Card>
  );
};

export default CallWidget;
